import React, { useState } from 'react';
import {
  Box, Typography, Chip, Paper, IconButton, Stack,
  Alert, Tabs, Tab, Button, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { ContentCopy, ExpandMore } from '@mui/icons-material';

const VARIABLES_BY_GAME_TYPE = {
  speed_game: [
    { name: 'schoolLevel', description: 'Niveau scolaire (CE2, CM1, etc.)', example: 'CE2' },
    { name: 'difficulty', description: 'Difficulté', example: 'medium' },
    { name: 'theme', description: 'Thème choisi', example: 'animaux' },
    { name: 'questionType', description: 'Type de question', example: 'spelling, grammar' },
    { name: 'competence.domain', description: 'Domaine de compétence', example: 'grammaire' },
    { name: 'competence.competence', description: 'Compétence', example: 'temps' },
    { name: 'competence.notion', description: 'Notion spécifique', example: 'présent' },
    { name: 'numberOfWords', description: 'Nombre de mots à générer', example: '20' },
    { name: 'numberOfOptions', description: 'Nombre d\'options par mot', example: '4' }
  ],
  dictation: [
    { name: 'level', description: 'Niveau scolaire', example: 'CE2' },
    { name: 'mode', description: 'Mode de dictée', example: 'training, word_lists' },
    { name: 'cleanedTheme', description: 'Thème (nettoyé)', example: 'les animaux' },
    { name: 'cleanedLesson', description: 'Leçon (nettoyée)', example: 'le présent' },
    { name: 'cleanedcustomwords', description: 'Mots personnalisés', example: 'chat, chien' },
    { name: 'totalWords', description: 'Nombre de mots', example: '20 mots' },
    { name: 'competence.domain', description: 'Domaine de compétence', example: 'grammaire' },
    { name: 'competence.competence', description: 'Compétence', example: 'temps' },
    { name: 'competence.notion', description: 'Notion spécifique', example: 'présent' }
  ],
  text_holes: [
    { name: 'schoolLevel', description: 'Niveau scolaire', example: 'CE2' },
    { name: 'difficulty', description: 'Difficulté', example: 'medium' },
    { name: 'theme', description: 'Thème', example: 'animaux' },
    { name: 'competence.domain', description: 'Domaine de compétence', example: 'grammaire' },
    { name: 'competence.competence', description: 'Compétence', example: 'temps' },
    { name: 'competence.notion', description: 'Notion spécifique', example: 'présent' }
  ],
  correction_game: [
    { name: 'schoolLevel', description: 'Niveau scolaire', example: 'CE2' },
    { name: 'difficulty', description: 'Difficulté', example: 'medium' },
    { name: 'theme', description: 'Thème', example: 'animaux' },
    { name: 'competence.domain', description: 'Domaine de compétence', example: 'grammaire' },
    { name: 'competence.competence', description: 'Compétence', example: 'temps' },
    { name: 'competence.notion', description: 'Notion spécifique', example: 'présent' }
  ],
  word_lists: [
    { name: 'schoolLevel', description: 'Niveau scolaire', example: 'CE2' },
    { name: 'difficulty', description: 'Difficulté', example: 'medium' },
    { name: 'theme', description: 'Thème', example: 'animaux' },
    { name: 'competence.domain', description: 'Domaine de compétence', example: 'grammaire' },
    { name: 'competence.competence', description: 'Compétence', example: 'temps' },
    { name: 'competence.notion', description: 'Notion spécifique', example: 'présent' }
  ]
};

const EXAMPLE_PROMPTS = {
  speed_game: `Génère un exercice de conjugaison au {{competence.notion}} pour des élèves de {{schoolLevel}}.

Le niveau de difficulté doit être {{difficulty}}.

Génère exactement {{numberOfWords}} mots avec {{numberOfOptions}} options chacun.

Les erreurs proposées doivent être adaptées au niveau {{schoolLevel}} et porter spécifiquement sur la conjugaison au {{competence.notion}}.`,

  dictation: `Génère une dictée de {{totalWords}} pour des élèves de {{level}}.

{{#if cleanedTheme}}
La dictée doit porter sur le thème : {{cleanedTheme}}
{{/if}}

{{#if cleanedLesson}}
Focus sur la leçon : {{cleanedLesson}}
{{/if}}

La dictée doit permettre de travailler la compétence {{competence.notion}}.`,

  text_holes: `Génère un texte à trous pour des élèves de {{schoolLevel}}.

Difficulté : {{difficulty}}

Schéma JSON : { "text", "blanks": [{ "answer", "distractors": [], "type" }], "completedText", "metadata" }.
Le texte doit porter sur {{competence.notion}} et contenir des trous pertinents pour cette compétence.`,
};

const VariableHelper = ({ gameType, onInsertVariable }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [copiedVar, setCopiedVar] = useState('');
  const [expandedVar, setExpandedVar] = useState(false);

  const variables = VARIABLES_BY_GAME_TYPE[gameType] || VARIABLES_BY_GAME_TYPE.speed_game;
  const examplePrompt = EXAMPLE_PROMPTS[gameType] || EXAMPLE_PROMPTS.speed_game;

  const handleInsertVariable = (varName) => {
    const varText = `{{${varName}}}`;
    if (onInsertVariable) {
      onInsertVariable(varText);
    }
    
    // Copier dans le presse-papier
    navigator.clipboard.writeText(varText);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(''), 2000);
  };

  const handleCopyExample = () => {
    navigator.clipboard.writeText(examplePrompt);
    setCopiedVar('example');
    setTimeout(() => setCopiedVar(''), 2000);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6">📝 Variables</Typography>
        <Typography variant="caption">
          Cliquez pour insérer
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        <Alert severity="info" sx={{ mb: 2, fontSize: '11px' }}>
          Syntaxe : <code>{'{{nomVariable}}'}</code>
        </Alert>

        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)} 
          sx={{ mb: 2, minHeight: '36px' }}
          variant="fullWidth"
        >
          <Tab label="Variables" sx={{ minHeight: '36px', fontSize: '12px' }} />
          <Tab label="Exemple" sx={{ minHeight: '36px', fontSize: '12px' }} />
        </Tabs>

        {/* Onglet Variables */}
        {activeTab === 0 && (
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              Type de jeu : <Chip label={gameType} size="small" />
            </Typography>
            
            {variables.map((variable) => (
              <Paper 
                key={variable.name} 
                sx={{ 
                  p: 1.5, 
                  bgcolor: 'grey.50',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' },
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
                onClick={() => handleInsertVariable(variable.name)}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontFamily: 'monospace', 
                    color: 'primary.main',
                    fontWeight: 'bold',
                    fontSize: '11px',
                    mb: 0.5
                  }}
                >
                  {'{{' + variable.name + '}}'}
                  {copiedVar === variable.name && (
                    <Chip label="✓" size="small" color="success" sx={{ ml: 1, height: '16px' }} />
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '10px' }}>
                  {variable.description}
                </Typography>
              </Paper>
            ))}
          </Stack>
        )}

        {/* Onglet Exemple */}
        {activeTab === 1 && (
          <Box>
            <Accordion expanded={expandedVar} onChange={() => setExpandedVar(!expandedVar)}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2">Voir l'exemple complet</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper sx={{ p: 1.5, bgcolor: 'grey.900', color: 'grey.100' }}>
                  <Typography 
                    variant="body2" 
                    component="pre" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', 
                      fontFamily: 'monospace', 
                      fontSize: '10px',
                      margin: 0
                    }}
                  >
                    {examplePrompt}
                  </Typography>
                </Paper>
              </AccordionDetails>
            </Accordion>

            <Button
              startIcon={<ContentCopy />}
              onClick={handleCopyExample}
              sx={{ mt: 1 }}
              fullWidth
              variant="outlined"
              size="small"
            >
              {copiedVar === 'example' ? '✓ Copié !' : 'Copier cet exemple'}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default VariableHelper;

